import { useEffect, useCallback } from 'react';

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  action: () => void;
  description: string;
}

interface UseKeyboardShortcutsProps {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
}

export const useKeyboardShortcuts = ({ shortcuts, enabled = true }: UseKeyboardShortcutsProps) => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Don't trigger shortcuts when typing in inputs
    if (event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLElement && event.target.contentEditable === 'true') {
      return;
    }

    const matchingShortcut = shortcuts.find(shortcut => {
      return shortcut.key.toLowerCase() === event.key.toLowerCase() &&
             !!shortcut.ctrlKey === event.ctrlKey &&
             !!shortcut.shiftKey === event.shiftKey &&
             !!shortcut.altKey === event.altKey &&
             !!shortcut.metaKey === event.metaKey;
    });

    if (matchingShortcut) {
      event.preventDefault();
      matchingShortcut.action();
    }
  }, [shortcuts, enabled]);

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleKeyDown, enabled]);
};

// Common keyboard shortcuts configuration
export const commonKeyboardShortcuts = (
  actions: {
    newChat?: () => void;
    toggleSidebar?: () => void;
    focusSearch?: () => void;
    saveFile?: () => void;
    toggleTheme?: () => void;
    openSettings?: () => void;
    navigateNext?: () => void;
    navigatePrev?: () => void;
  }
): KeyboardShortcut[] => [
  {
    key: 'n',
    ctrlKey: true,
    action: actions.newChat || (() => {}),
    description: 'New chat',
  },
  {
    key: 'b',
    ctrlKey: true,
    action: actions.toggleSidebar || (() => {}),
    description: 'Toggle sidebar',
  },
  {
    key: 'k',
    ctrlKey: true,
    action: actions.focusSearch || (() => {}),
    description: 'Focus search',
  },
  {
    key: 's',
    ctrlKey: true,
    action: actions.saveFile || (() => {}),
    description: 'Save file',
  },
  {
    key: 't',
    ctrlKey: true,
    shiftKey: true,
    action: actions.toggleTheme || (() => {}),
    description: 'Toggle theme',
  },
  {
    key: ',',
    ctrlKey: true,
    action: actions.openSettings || (() => {}),
    description: 'Open settings',
  },
  {
    key: 'ArrowRight',
    ctrlKey: true,
    action: actions.navigateNext || (() => {}),
    description: 'Navigate next',
  },
  {
    key: 'ArrowLeft',
    ctrlKey: true,
    action: actions.navigatePrev || (() => {}),
    description: 'Navigate previous',
  },
];